import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { NotificationsOutlined, Email, PhoneIphone } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ErrorIcon } from '@eposnow/ui-core';
import { useNavigate } from 'react-router-dom';
import NotificationsSettingsContainer from './NotificationsSettingsContainer';
import { useNotifications } from '../../api/notifications';
import { AssociativeArray, defer } from '../../helpers/helpers';
import { Channel, UserNotification } from '../../models/notifications';
import { EMAIL, IN_APP, SMS } from '../../constants';
import EnhancedSnackbar, { Snackbar } from '../../components/EnhancedSnackbar';
import GenericErrorPage from './GenericErrorPage';
import { ErrorObject } from '../../api/fetch';
import { ReactComponent as ServerIcon } from '../../img/ServerIcon.svg';
import { ReactComponent as UnderMaintenance } from '../../img/underMaintenance.svg';
import ChannelButtons from './ChannelButtons';

export type ChannelKeys = typeof IN_APP | typeof EMAIL | typeof SMS;

const Notifications = () => {
  const { t } = useTranslation();
  const [activeButton, setActiveButton] = useState<ChannelKeys>(IN_APP);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<Snackbar | undefined>(undefined);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [currentChannel, setCurrentChannel] = useState<Channel>();
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    isError,
    isLoading,
    error,
    data: notificationsData,
    refetch,
  } = useNotifications<UserNotification>('notifications');

  const buttonData: AssociativeArray = {
    [IN_APP]: {
      title: t('screens.settings.inProduct'),
      description: t('screens.settings.inProductDescription'),
      icon: <NotificationsOutlined />,
    },
    [EMAIL]: {
      title: t('screens.settings.email'),
      description: t('screens.settings.inEmailDescription'),
      icon: <Email />,
    },
    [SMS]: {
      title: t('screens.settings.sms'),
      description: t('screens.settings.inSmsDescription'),
      icon: <PhoneIphone />,
    },
  };

  const showSavedMessage = (success = false) => {
    if (success)
      setSnackbar({ severity: 'success', message: t('screens.settings.successfullySaved') });
    else setSnackbar(undefined);
    defer(() => setSnackbarOpen(true));
  };

  const setChannel = (channelKey: ChannelKeys) => {
    const channel = channels.find((ch) => ch.displayKey === channelKey);
    setCurrentChannel(channel);
    setActiveButton(channel?.displayKey as ChannelKeys);
  };

  const handleClick = (id: ChannelKeys) => {
    setChannel(id);
  };

  const hasSetInitialChannel = React.useRef(false);

  useEffect(() => {
    if (notificationsData?.channels?.length) {
      setChannels(notificationsData.channels);
    }
  }, [notificationsData]);

  useEffect(() => {
    if (!hasSetInitialChannel.current && channels.length) {
      setChannel(IN_APP);
      hasSetInitialChannel.current = true;
    }
  }, [channels]);

  const handleUpdateChannel = (updatedChannel: Channel) => {
    setChannels((prev) =>
      prev.map((c) => (c.id === updatedChannel.id ? updatedChannel : c))
    );
    setCurrentChannel(updatedChannel);
  };

  if (process.env.REACT_APP_DATA_NOTIFICATIONS_MAINTENANCE === 'true')
    return (
      <GenericErrorPage
        icon={<UnderMaintenance />}
        title={t('components.iconMessage.errorPage.underMaintenance')}
        description={t('components.iconMessage.errorPage.underMaintenanceDescription')}
        rightButtonText={t('components.iconMessage.errorPage.back')}
        actionRightButton={() => navigate(-1)}
      />
    );

  if (isError) {
    const status = (error as ErrorObject)?.code;

    if (status === 400) {
      return (
        <GenericErrorPage
          icon={<ErrorIcon />}
          title={t('components.iconMessage.errorPage.badRequest')}
          description={t('components.iconMessage.errorPage.noProcessRequest')}
          leftButtonText={t('components.iconMessage.errorPage.back')}
          rightButtonText={t('components.iconMessage.errorPage.retry')}
          actionLeftButton={() => navigate(-1)}
          actionRightButton={() => refetch()}
        />
      );
    }

    if (status === 429) {
      return (
        <GenericErrorPage
          icon={<ServerIcon />}
          title={t('components.iconMessage.errorPage.serverBusy')}
          description={t('components.iconMessage.errorPage.serveBusyText')}
          leftButtonText={t('components.iconMessage.errorPage.back')}
          rightButtonText={t('components.iconMessage.errorPage.retry')}
          actionLeftButton={() => navigate(-1)}
          actionRightButton={() => refetch()}
        />
      );
    }

    return (
      <GenericErrorPage
        icon={<ServerIcon />}
        title={t('components.iconMessage.errorPage.internalServerError')}
        description={t('components.iconMessage.errorPage.internalServerErrorText')}
        leftButtonText={t('components.iconMessage.errorPage.status')}
        rightButtonText={t('components.iconMessage.errorPage.back')}
        actionLeftButton={() => {
          window.location.href = `${process.env.REACT_APP_LINK_STATUS_PAGE}`;
        }}
        actionRightButton={() => navigate(-1)}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          margin: { xs: '8px', md: '24px 32px' },
        }}
      >
        <Box
          sx={{
            flex: '0 0 25%',
            display: 'flex',
            flexDirection: 'column',
            marginRight: '16px',
            marginBottom: { xs: '16px' },
          }}
        >
          <Typography sx={{ fontSize: '32px', fontWeight: 600, marginBottom: '8px', fontFamily: 'Poppins, sans-serif' }}>
            {t('screens.settings.notificationsTitle')}
          </Typography>
          <Typography variant="body1">{t('screens.settings.notificationsDescription')}</Typography>
        </Box>

        <Box sx={{ flex: '1', display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
            {!isLoading && notificationsData?.channels && (
              <ChannelButtons
                notificationsData={notificationsData}
                activeButtonKey={activeButton}
                buttonData={buttonData}
                handleClick={handleClick}
              />
            )}
          </Box>

          <Box>
            {!isLoading && currentChannel && (
              <NotificationsSettingsContainer
                description={buttonData[activeButton]?.description}
                channel={currentChannel}
                onUpdateChannel={handleUpdateChannel}
                showSnackbar={showSavedMessage}
                refetch={refetch}
              />
            )}
          </Box>
        </Box>
      </Box>

      <EnhancedSnackbar
        open={snackbarOpen}
        severity={snackbar?.severity || 'error'}
        message={snackbar?.message || t('screens.settings.changesNotSaved')}
        forceColor={
          snackbar?.severity
            ? theme?.palette?.elevation?.['12dp']
            : theme?.palette?.error?.['160p']
        }
        onClose={() => {
          setSnackbarOpen(false);
        }}
      />
    </>
  );
};

export default Notifications;
